import { useMemo, useState } from "react";

import TerritoryLevel from "@mapmycustomers/shared/enum/TerritoryLevel";
import AreaRegion from "@mapmycustomers/shared/types/territory/AreaRegion";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";
import LookupData from "@mapmycustomers/shared/types/territory/LookupData";
import { parseAreaType } from "@mapmycustomers/shared/util/territory/areaTypes";

import { useMap, useMapEventHandler } from "@app/component/map/FeaturedMap";
import useAreaTypeMetaData from "@app/scene/map/components/TerritoriesMode/utils/useAreaTypeMetaData";
import useBoundaries from "@app/scene/map/components/TerritoriesMode/utils/useBoundaries";

const defaultThresholdLevels: [adm2Threshold: number, adm3Threshold: number] = [6, 9];

const useDynamicBoundaries = (
  areaType: AreaType,
  lookupData: LookupData,
  selectedRegionIds: AreaRegion["id"][],
  thresholdZoomLevels: [adm2Threshold: number, adm3Threshold: number] = defaultThresholdLevels
): [
  currentLevel: TerritoryLevel,
  regions: AreaRegion[],
  selectedCurrentRegionIds: AreaRegion["id"][],
  fullySelectedCurrentRegionIds: AreaRegion["id"][]
] => {
  const { map } = useMap();
  const [zoom, setZoom] = useState(map.getZoom()!);
  useMapEventHandler("zoom_changed", () => {
    setZoom(map.getZoom()!);
  });

  const { countryCode, level } = parseAreaType(areaType)!;
  const currentLevel = useMemo(
    () => {
      const [adm2Threshold, adm3Threshold] = thresholdZoomLevels;
      if (zoom < adm2Threshold) {
        return TerritoryLevel.ADM1;
      } else if (zoom < adm3Threshold && level !== TerritoryLevel.ADM1) {
        return TerritoryLevel.ADM2;
      } else {
        return level;
      }
    },
    // we use spread to not have a dep on thresholdZoomLevels array instance, we only care about values
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [zoom, level, ...thresholdZoomLevels]
  );

  const currentAreaType = `${countryCode}_${currentLevel}` as AreaType;
  const [currentMetaData] = useAreaTypeMetaData(currentAreaType);
  const [currentRegions] = useBoundaries(currentAreaType, currentMetaData.files);

  const [selectedCurrentRegionIds, fullySelectedCurrentRegionIds] = useMemo(() => {
    if (level === currentLevel) {
      return [selectedRegionIds, selectedRegionIds];
    }

    const selectedRegionIdsSet = new Set(selectedRegionIds);

    const fullySelectedCurrentRegionIds: AreaRegion["id"][] = [];
    const selectedCurrentRegionIds: AreaRegion["id"][] = [];
    Object.keys(
      // We know that currentLevel can never be ADM3 at this point
      // @ts-ignore
      lookupData[currentLevel]
    ).forEach((regionId) => {
      // Same reasons as above
      // @ts-ignore
      const candidates: AreaRegion["id"][] = lookupData[currentLevel][regionId]?.[level] ?? [];
      const selectedCount = candidates.reduce(
        (count, candidateId) => count + (selectedRegionIdsSet.has(candidateId) ? 1 : 0),
        0
      );
      if (selectedCount > 0) {
        selectedCurrentRegionIds.push(regionId);
        if (selectedCount === candidates.length) {
          fullySelectedCurrentRegionIds.push(regionId);
        }
      }
    });
    return [selectedCurrentRegionIds, fullySelectedCurrentRegionIds];
  }, [lookupData, selectedRegionIds, level, currentLevel]);

  return [currentLevel, currentRegions, selectedCurrentRegionIds, fullySelectedCurrentRegionIds];
};

export default useDynamicBoundaries;
